import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SvgNmrIntegrate, SvgNmrPeaks, SvgNmrPeaksTopLabels, } from 'cheminfo-font';
import lodashGet from 'lodash/get';
import { rangesToACS } from 'nmr-processing';
import { FaFileExport, FaUnlink, FaSitemap, FaChartBar } from 'react-icons/fa';
import { ImLink } from 'react-icons/im';
import { LuSubtitles } from 'react-icons/lu';
import { ClipboardFallbackModal } from '../../../utils/clipboard/clipboardComponents';
import { useClipboard } from '../../../utils/clipboard/clipboardHooks';
import { useAssignmentData } from '../../assignment/AssignmentsContext';
import { useDispatch } from '../../context/DispatchContext';
import { useToaster } from '../../context/ToasterContext';
import { useModal } from '../../elements/popup/Modal';
import { useActiveSpectrumRangesViewState } from '../../hooks/useActiveSpectrumRangesViewState';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import CopyClipboardModal from '../../modal/CopyClipboardModal';
import ChangeSumModal from '../../modal/changeSum/ChangeSumModal';
import { booleanToString } from '../../utility/booleanToString';
import DefaultPanelHeader from '../header/DefaultPanelHeader';
function RangesHeader({ ranges, info, onUnlink, onFilterActivated, onSettingClick, isFilterActive, filterCounter, activeTab, }) {
    const dispatch = useDispatch();
    const modal = useModal();
    const toaster = useToaster();
    const assignmentData = useAssignmentData();
    const currentSum = lodashGet(ranges, 'options.sum', null);
    const rangesPreferences = usePanelPreferences('ranges', activeTab);
    const { showMultiplicityTrees, showJGraph, showIntegrals, showIntegralsValues, showPeaks, displayingMode, showAssignmentsLabels, } = useActiveSpectrumRangesViewState();
    function changeRangesSumHandler(options) {
        dispatch({ type: 'CHANGE_RANGE_SUM', payload: { options } });
        modal.close();
    }
    function removeAssignments() {
        onUnlink();
    }
    function handleOnRemoveAssignments() {
        modal.showConfirmDialog({
            message: 'All assignments will be removed. Are you sure?',
            buttons: [{ text: 'Yes', handler: removeAssignments }, { text: 'No' }],
        });
    }
    function handleDeleteAll() {
        modal.showConfirmDialog({
            message: 'All ranges will be deleted. Are You sure?',
            buttons: [
                {
                    text: 'Yes',
                    handler: () => {
                        dispatch({
                            type: 'DELETE_RANGE',
                            payload: { assignmentData },
                        });
                    },
                },
                { text: 'No' },
            ],
        });
    }
    function handleSetShowMultiplicityTrees() {
        dispatch({
            type: 'TOGGLE_RANGES_VIEW_PROPERTY',
            payload: { key: 'showMultiplicityTrees' },
        });
    }
    function handleShowIntegralsValues() {
        dispatch({
            type: 'TOGGLE_RANGES_VIEW_PROPERTY',
            payload: { key: 'showIntegralsValues' },
        });
    }
    function handleShowIntegrals() {
        dispatch({
            type: 'TOGGLE_RANGES_VIEW_PROPERTY',
            payload: { key: 'showIntegrals' },
        });
    }
    function handleShowJGraph() {
        dispatch({
            type: 'TOGGLE_RANGES_VIEW_PROPERTY',
            payload: { key: 'showJGraph' },
        });
    }
    function handleShowAssignmentsLabel() {
        dispatch({
            type: 'TOGGLE_RANGES_VIEW_PROPERTY',
            payload: { key: 'showAssignmentsLabels' },
        });
    }
    const { rawWriteWithType, shouldFallback, cleanShouldFallback, text } = useClipboard();
    function saveToClipboardHandler(value) {
        void rawWriteWithType(value, 'text/html').then(() => toaster.show({ message: 'Data copied to clipboard', intent: 'success' }));
    }
    function saveAsHTMLHandler() {
        if (Array.isArray(ranges?.values) && ranges.values.length > 0) {
            const { originFrequency: observedFrequency, nucleus } = info;
            const result = rangesToACS(ranges.values, {
                nucleus, // '19f'
                deltaFormat: rangesPreferences.deltaPPM.format,
                couplingFormat: rangesPreferences.coupling.format,
                observedFrequency, //400
            });
            modal.show(_jsx(CopyClipboardModal, { text: result, onCopyClick: saveToClipboardHandler, onClose: () => modal.close() }), {});
        }
    }
    function changeSumConstantFlagHandler() {
        dispatch({
            type: 'CHANGE_RANGES_SUM_FLAG',
        });
    }
    function toggleViewProperty(key) {
        dispatch({ type: 'TOGGLE_RANGES_VIEW_PROPERTY', payload: { key } });
    }
    function toggleDisplayingMode() {
        dispatch({ type: 'TOGGLE_RANGES_PEAKS_DISPLAYING_MODE' });
    }
    const hasRanges = Array.isArray(ranges?.values) && ranges.values.length > 0;
    const total = ranges?.values?.length || 0;
    return (_jsxs("div", { children: [_jsx(DefaultPanelHeader, { total: total, counter: filterCounter, onDelete: handleDeleteAll, deleteToolTip: "Delete All Ranges", onFilter: onFilterActivated, filterToolTip: isFilterActive ? 'Show all ranges' : 'Hide ranges out of view', onSettingClick: onSettingClick, leftButtons: [
                    {
                        disabled: !hasRanges,
                        icon: _jsx(FaFileExport, {}),
                        tooltip: 'Preview publication string',
                        onClick: saveAsHTMLHandler,
                    },
                    {
                        component: (_jsx(ChangeSumModal, { onSave: changeRangesSumHandler, sumType: "ranges", currentSum: currentSum, sumOptions: ranges?.options })),
                    },
                    {
                        icon: _jsx(ImLink, {}),
                        tooltip: 'Fixed integration sum',
                        onClick: changeSumConstantFlagHandler,
                        active: ranges?.options?.isSumConstant,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(FaUnlink, {}),
                        tooltip: 'Remove all assignments',
                        onClick: handleOnRemoveAssignments,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(FaSitemap, {}),
                        tooltip: `${booleanToString(!showMultiplicityTrees)} multiplicity trees in spectrum`,
                        onClick: handleSetShowMultiplicityTrees,
                        active: showMultiplicityTrees,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(FaChartBar, {}),
                        tooltip: `${booleanToString(!showJGraph)} J Graph`,
                        onClick: handleShowJGraph,
                        active: showJGraph,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(SvgNmrIntegrate, {}),
                        tooltip: `${booleanToString(!showIntegrals)} integrals`,
                        onClick: handleShowIntegrals,
                        active: showIntegrals,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(SvgNmrIntegrate, {}),
                        tooltip: `${booleanToString(!showIntegralsValues)} integrals values`,
                        onClick: handleShowIntegralsValues,
                        active: showIntegralsValues,
                    },
                    {
                        id: 'ranges-toggle-peaks',
                        disabled: !hasRanges,
                        icon: _jsx(SvgNmrPeaks, {}),
                        tooltip: `${booleanToString(!showPeaks)} peaks`,
                        onClick: () => toggleViewProperty('showPeaks'),
                        active: showPeaks,
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(SvgNmrPeaksTopLabels, {}),
                        tooltip: `${displayingMode === 'spread' ? 'Single' : 'Spread'} mode`,
                        onClick: toggleDisplayingMode,
                        active: displayingMode === 'spread',
                    },
                    {
                        disabled: !hasRanges,
                        icon: _jsx(LuSubtitles, {}),
                        tooltip: `${booleanToString(!showAssignmentsLabels)} assignments labels`,
                        onClick: handleShowAssignmentsLabel,
                        active: showAssignmentsLabels,
                    },
                ] }), _jsx(ClipboardFallbackModal, { mode: shouldFallback, onDismiss: cleanShouldFallback, text: text, label: "Preview publication string" })] }));
}
export default RangesHeader;
